import React, { Dispatch, SetStateAction } from 'react';

import { UseOutletContextSale } from '../Sale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Invoice } from '../../../../models/invoice.model';
import { RequirementContent } from '../../../../models/requirement.content.model';
import { accountingAccountSelected } from '../../../../scripts/list.selected.script';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { accountingAccountCoefficientList } from '../../../../scripts/list.filter.script';
import { accountingAccountId } from '../../../../scripts/item.account.script';

import AppInputAccountingAccount from '../../../../components/element/InputAccountingAccount';

export interface AppSaleFormEntriesProps {
  component?: string,
  invoice: Invoice,
  contents: {value: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: boolean[]},
  amounts: {value: {base: number, tax: number, rebate: number, content: number, debit: number, credit: number}, valid: boolean},
  setContents: Dispatch<SetStateAction<{value: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: boolean[]}>>,
  setAmounts: Dispatch<SetStateAction<{value: {base: number, tax: number, rebate: number, content: number, debit: number, credit: number}, valid: boolean}>>,
  companyForUserProp?: any | null,
  settingsForCompanyProp?: any | null,
  accountingAccountsForCompanyProp?: any[] | null,
  accountingIncomeAccountsForCompanyProp?: any[] | null,
};

const AppSaleFormEntries: React.FunctionComponent<AppSaleFormEntriesProps> = ({
  component, invoice, contents, amounts, setContents, setAmounts,
  companyForUserProp, settingsForCompanyProp, accountingAccountsForCompanyProp, accountingIncomeAccountsForCompanyProp,
}) => {
  let ctx: any = {}
  try { ctx = UseOutletContextSale() ?? {} } catch { ctx = {} }

  const companyForUser = companyForUserProp ?? ctx.companyForUser
  const settingsForCompany = settingsForCompanyProp ?? ctx.settingsForCompany
  const accountingAccountsForCompany = accountingAccountsForCompanyProp ?? ctx.accountingAccountsForCompany
  const accountingIncomeAccountsForCompany = accountingIncomeAccountsForCompanyProp ?? ctx.accountingIncomeAccountsForCompany
  const {lang} = StateLanguage()

  const allowedDebitSaleRebate = (code: string): boolean => {
    const c = String(code);
    if (!c.startsWith('3') && !c.startsWith('4')) return false;
    const base = c.split('.')[0];
    if (base === '3809' || base === '3790') return false;
    if (base === '3800' || base === '3801' || base === '3802' || base === '3619') return true;
    return base.endsWith('09') || base.endsWith('90') || base.endsWith('91') || base.endsWith('92');
  };

  const buildSaleRebateAccountList = (allAccounts: any[] | null): any[] => {
    if (!allAccounts) return [];
    return allAccounts.filter((a: any) => a.type === 'leave' && allowedDebitSaleRebate(a.code));
  };

  return (
    <div className="dataTables_wrapper">
      { companyForUser && accountingAccountsForCompany && accountingIncomeAccountsForCompany &&
        <div className="table-responsive">
          <table className="table align-middle table-row-dashed gy-1 mb-2">
            <thead className="border-bottom">
              <tr className="text-muted text-uppercase fw-bolder fs-7">
                <th className="min-w-200px text-nowrap text-start">{lang.labels.description}</th>
                <th className="w-350px text-nowrap text-end">{lang.labels.debit}</th>
                <th className="w-350px text-nowrap text-end">{lang.labels.credit}</th>
              </tr>
            </thead>
            <tbody className="text-muted fw-bold">
              { contents.value.map (( (item, index) => { return (
                <tr key={index}>
                  <td className="text-secondary text-start">{item.content.detail}</td>
                  <td className="text-nowrap text-end">
                    { ((item.content.rebate && item.content.rebate.type !== "none") || (!item.content.rebate && item.content.rebate_amount > 0)) &&
                      <div className="d-flex flex-column">
                        <span className="form-select-sm">{accountingAccountSelected(accountingAccountsForCompany, item.debit.accounting_account, companyForUser.language)}</span>
                        <strong className="fs-8">{invoice.currency.code} {formatMoney(item.content.rebate_amount)}</strong>
                      </div>
                    }
                    { item.content.price_unit < 0 &&
                      <div className="d-flex flex-column">
                        <AppInputAccountingAccount component={component} options={{small: true, type: "debit", index: index, item: item}} companyForUser={companyForUser} accountingAccountsForCompany={accountingAccountsForCompany} accountingIncomeAccountsForCompany={buildSaleRebateAccountList(accountingAccountsForCompany)} contentAccounts={contents} setContentAccounts={setContents}></AppInputAccountingAccount>
                        <strong className="fs-8">{invoice.currency.code} {formatMoney(item.debit.amount)}</strong>
                      </div>
                    }
                  </td>
                  <td className="text-nowrap text-end">
                    { item.content.price_unit < 0 &&
                      <div className="d-flex flex-column">
                        <span className="form-select-sm">{accountingAccountSelected(accountingAccountsForCompany, item.credit.accounting_account, companyForUser.language)}</span>
                        <strong className="fs-8">{invoice.currency.code} {formatMoney(item.credit.amount)}</strong>
                      </div>
                    }
                    { item.content.price_unit >= 0 &&
                      <div className="d-flex flex-column">
                        <AppInputAccountingAccount component={component} options={{small: true, type: "credit", index: index, item: item}} companyForUser={companyForUser} accountingAccountsForCompany={accountingAccountsForCompany} accountingIncomeAccountsForCompany={accountingAccountCoefficientList('negative', accountingIncomeAccountsForCompany)} contentAccounts={contents} setContentAccounts={setContents}></AppInputAccountingAccount>
                        <strong className="fs-8">{invoice.currency.code} {formatMoney(item.credit.amount)}</strong>
                      </div>
                    }
                  </td>
                </tr>
              )}))}
            </tbody>
            <tfoot className="border-top text-secondary fw-bolder fs-7">
              <tr>
                <td>
                  <div className="d-flex flex-column text-start text-nowrap">
                    <span className="fs-8">{lang.labels.customerReceivable}</span>
                    <span className="fw-bold">{formatContact(invoice.customer)}</span>
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column text-end text-nowrap">
                    <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accountingAccountId(accountingAccountsForCompany, "1100"), companyForUser.language)}</span>
                    <span className="fs-8">{invoice.currency.code} {formatMoney(amounts.value.base)}</span>
                  </div>
                </td>
                <td></td>
              </tr>
              { (settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false) && amounts.value.tax !== 0 &&
                <tr>
                  <td>
                    <div className="d-flex flex-column text-start text-nowrap">
                      <span className="fs-8">{lang.labels.vatDue}</span>
                      <span className="fw-bold">{invoice.invoice_number}</span>
                    </div>
                  </td>
                  <td>
                    { ((settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective') === "tdfn" || (settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective') === "taf") &&
                      <div className="d-flex flex-column text-end text-nowrap">
                        <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accountingAccountId(accountingAccountsForCompany, "3809"), companyForUser.language)}</span>
                        <span className="fs-8">{invoice.currency.code} {formatMoney(amounts.value.tax)}</span>
                      </div>
                    }
                  </td>
                  <td>
                    <div className="d-flex flex-column text-end text-nowrap">
                      <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accountingAccountId(accountingAccountsForCompany, "2200"), companyForUser.language)}</span>
                      <span className="fs-8">{invoice.currency.code} {formatMoney(amounts.value.tax)}</span>
                    </div>
                  </td>
                </tr>
              }
              <tr>
                <td className="text-start text-uppercase">{lang.labels.total}</td>
                <td className="text-end text-nowrap">{invoice.currency.code} {formatMoney(amounts.value.debit)}</td>
                <td className="text-end text-nowrap">{invoice.currency.code} {formatMoney(amounts.value.credit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      }
    </div>
  )
};

export default AppSaleFormEntries;
